import { replayPrivacyOptions } from './replay-privacy.ts';

export type ReplayMode = 'continuous' | 'on-error';

export interface ReplayRecorderInputs {
  mode: ReplayMode;
  /** Ring-buffer length in on-error mode; sets how often a FullSnapshot checkpoint is taken. */
  bufferMs: number;
  maskAllText?: boolean;
}

/**
 * Builds the rrweb `record()` options shared by every app, minus the `emit`
 * callback. Images are recorded by URL rather than inlined: inlining redraws
 * each live `<img>` through a canvas and rewrites its `crossorigin` attribute
 * when the copy is cross-origin, which fails against the browser's cached
 * plain copy and fires the element's own error handler. Stylesheets are
 * inlined so a replay renders styled inside the Workbench origin.
 *
 * @param inputs the replay mode, the on-error buffer length in milliseconds, and
 *   whether every text node is masked rather than only the flagged ones
 * @returns the options to spread into `record()`; in on-error mode the checkpoint
 *   interval is half the buffer (never under 15 s) so a FullSnapshot always sits
 *   inside the retained window, in continuous mode it is five minutes
 */
export function replayRecorderOptions({ mode, bufferMs, maskAllText }: ReplayRecorderInputs) {
  return {
    ...replayPrivacyOptions(maskAllText),
    inlineImages: false,
    inlineStylesheet: true,
    checkoutEveryNms: mode === 'on-error' ? Math.max(bufferMs / 2, 15_000) : 300_000,
    sampling: {
      mousemove: 200,
      mouseInteraction: true,
      scroll: 300,
      input: 'last' as const,
    },
  };
}
